type NotificationLevel = 'info' | 'warn' | 'error' | 'alert';

export interface SystemNotification {
  id: string;
  timestamp: number;
  moduleId: string;
  title: string;
  message: string;
  level: NotificationLevel;
}

export interface NotificationToggles {
  globalEnabled: boolean;
  moduleMutes: Record<string, boolean>;
}

export class NotificationService {
  private static instance: NotificationService;
  private ringBuffer: SystemNotification[] = [];
  private readonly MAX_BUFFER_SIZE = 100;
  
  private toggles: NotificationToggles = {
    globalEnabled: true,
    moduleMutes: {}
  };

  private listeners: Set<(notification: SystemNotification) => void> = new Set();

  private constructor() {
    this.loadToggles();
  }

  public static getInstance(): NotificationService {
    if (!NotificationService.instance) {
      NotificationService.instance = new NotificationService();
    }
    return NotificationService.instance;
  }

  private loadToggles() {
    try {
      const saved = localStorage.getItem('mesh_notification_toggles');
      if (saved) {
        this.toggles = JSON.parse(saved);
      }
    } catch {}
  }

  public saveToggles(toggles: Partial<NotificationToggles>) {
    this.toggles = { ...this.toggles, ...toggles };
    try {
      localStorage.setItem('mesh_notification_toggles', JSON.stringify(this.toggles));
    } catch {}
  }

  public getToggles(): NotificationToggles {
    return this.toggles;
  }

  public subscribe(fn: (notification: SystemNotification) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  public dispatch(moduleId: string, title: string, message: string, level: NotificationLevel = 'info') {
    if (!this.toggles.globalEnabled) return;
    if (this.toggles.moduleMutes[moduleId]) return;

    const notification: SystemNotification = {
      id: crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2),
      timestamp: Date.now(),
      moduleId,
      title,
      message,
      level,
    };

    this.ringBuffer.push(notification);
    if (this.ringBuffer.length > this.MAX_BUFFER_SIZE) {
      this.ringBuffer.shift(); // evict oldest
    }

    for (const listener of this.listeners) {
      try {
        listener(notification);
      } catch (err) {
        console.error('Error in notification listener', err);
      }
    }
  }

  public getBuffer(): SystemNotification[] {
    return [...this.ringBuffer];
  }
}

export const notificationService = NotificationService.getInstance();
